/**
 * Internet Connectivity Service
 *
 * Provides centralized internet connectivity detection for the entire app.
 * Combines navigator.onLine detection with active connection testing.
 */

import eventBus from '../utils/eventBus';

// Consistent log prefix for easy filtering
const LOG_PREFIX = '[INTERNET_CONNECTIVITY]';

class InternetConnectivityService {
    constructor() {
        this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
        this.hasInternet = false;
        this.listeners = new Set();
        this.checkingConnection = false;
        this.lastCheckTime = 0;
        this.checkInterval = 5000; // 5 seconds between checks
        this.timeoutDuration = 3000; // 3 seconds timeout for connection test

        console.log(`${LOG_PREFIX} Service initialized - Navigator online: ${this.isOnline}`);

        this.initializeEventListeners();
        this.checkInternetConnection();
    }

    /**
     * Initialize browser online/offline event listeners
     */
    initializeEventListeners() {
        if (typeof window === 'undefined') {
            console.log(`${LOG_PREFIX} Skipping event listeners - no window object`);
            return;
        }

        console.log(`${LOG_PREFIX} Setting up browser online/offline event listeners`);

        const updateOnlineStatus = () => {
            const wasOnline = this.isOnline;
            this.isOnline = navigator.onLine;

            console.log(`${LOG_PREFIX} Navigator online status changed: ${wasOnline} → ${this.isOnline}`);

            if (this.isOnline && !wasOnline) {
                console.log(`${LOG_PREFIX} Just came online - testing connection`);
                this.checkInternetConnection();
            } else if (!this.isOnline) {
                console.log(`${LOG_PREFIX} Went offline - marking as no internet`);
                this.hasInternet = false;
                this.notifyListeners();
            }
        };

        window.addEventListener('online', updateOnlineStatus);
        window.addEventListener('offline', updateOnlineStatus);

        // Store references for cleanup
        this.onlineHandler = updateOnlineStatus;
        console.log(`${LOG_PREFIX} Event listeners registered successfully`);
    }

    /**
     * Test actual internet connectivity by making a request
     * Uses multiple fallback endpoints for reliability
     */
    async checkInternetConnection(force = false) {
        // Rate limiting - don't check too frequently unless forced
        const now = Date.now();
        if (!force && this.checkingConnection) {
            console.log(`${LOG_PREFIX} Connection check already in progress, skipping`);
            return this.hasInternet;
        }

        if (!force && (now - this.lastCheckTime) < this.checkInterval) {
            const timeLeft = Math.round((this.checkInterval - (now - this.lastCheckTime)) / 1000);
            console.log(`${LOG_PREFIX} Rate limiting connection check (${timeLeft}s remaining)`);
            return this.hasInternet;
        }

        this.checkingConnection = true;
        this.lastCheckTime = now;

        // If navigator says we're offline, don't bother testing
        if (!this.isOnline) {
            console.log(`${LOG_PREFIX} Navigator reports offline, skipping connection test`);
            this.hasInternet = false;
            this.checkingConnection = false;
            this.notifyListeners();
            return false;
        }

        console.log(`${LOG_PREFIX} Starting internet connectivity test (force: ${force})...`);

        // Test endpoints in order of preference
        const testEndpoints = [
            'https://www.google.com',
            'https://api.cloudflare.com/client/v4',
            'https://httpbin.org/get',
            'https://jsonplaceholder.typicode.com/posts/1'
        ];

        for (const endpoint of testEndpoints) {
            try {
                console.log(`${LOG_PREFIX} Testing endpoint: ${endpoint}`);

                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), this.timeoutDuration);

                const startTime = Date.now();
                const response = await fetch(endpoint, {
                    mode: 'no-cors',
                    cache: 'no-cache',
                    signal: controller.signal
                });

                clearTimeout(timeoutId);
                const responseTime = Date.now() - startTime;

                // If we get here, we have internet connectivity
                console.log(`${LOG_PREFIX} ✅ Internet connection confirmed via ${endpoint} (${responseTime}ms)`);
                this.hasInternet = true;
                this.checkingConnection = false;
                this.notifyListeners();
                return true;

            } catch (error) {
                console.log(`${LOG_PREFIX} ❌ Failed to reach ${endpoint}: ${error.message}`);
                continue; // Try next endpoint
            }
        }

        // All endpoints failed
        console.log(`${LOG_PREFIX} ⚠️ No internet connection available - all ${testEndpoints.length} endpoints failed`);
        this.hasInternet = false;
        this.checkingConnection = false;
        this.notifyListeners();
        return false;
    }

    /**
     * Get current connectivity status
     * @returns {Object} { isOnline: boolean, hasInternet: boolean, isConnected: boolean }
     */
    getStatus() {
        return {
            isOnline: this.isOnline,           // Navigator online/offline
            hasInternet: this.hasInternet,     // Actual internet connectivity
            isConnected: this.isOnline && this.hasInternet  // Both conditions met
        };
    }

    /**
     * Check if the app should bypass internet-dependent features
     * @returns {boolean} true if internet features should be bypassed
     */
    shouldBypassInternetFeatures() {
        return !this.hasInternet;
    }

    /**
     * Check if the app is completely offline
     * @returns {boolean} true if completely offline
     */
    isCompletelyOffline() {
        return !this.isOnline || !this.hasInternet;
    }

    /**
     * Force a connectivity check regardless of rate limiting
     * @returns {Promise<boolean>} true if internet is available
     */
    async forceCheck() {
        return await this.checkInternetConnection(true);
    }

    /**
     * Subscribe to connectivity changes
     * @param {Function} callback - Called with { isOnline, hasInternet, isConnected }
     * @returns {Function} unsubscribe function
     */
    subscribe(callback) {
        this.listeners.add(callback);

        // Immediately call with current status
        callback(this.getStatus());

        // Return unsubscribe function
        return () => {
            this.listeners.delete(callback);
        };
    }

    /**
     * Notify all subscribers of connectivity changes
     */
    notifyListeners() {
        const status = this.getStatus();
        console.log(`${LOG_PREFIX} 📡 Status update - Online: ${status.isOnline}, Internet: ${status.hasInternet}, Connected: ${status.isConnected} (${this.listeners.size} listeners)`);

        let successCount = 0;
        let errorCount = 0;

        this.listeners.forEach(callback => {
            try {
                callback(status);
                successCount++;
            } catch (error) {
                console.error(`${LOG_PREFIX} ❌ Error in listener callback:`, error);
                errorCount++;
            }
        });

        if (errorCount > 0) {
            console.warn(`${LOG_PREFIX} ⚠️ ${errorCount} listener callbacks failed, ${successCount} succeeded`);
        }

        // Also emit via event bus for components that prefer event-driven approach
        eventBus.emit('connectivityChanged', status);
        console.log(`${LOG_PREFIX} 🔄 Event emitted via eventBus: connectivityChanged`);
    }

    /**
     * Clean up event listeners
     */
    destroy() {
        if (typeof window !== 'undefined' && this.onlineHandler) {
            window.removeEventListener('online', this.onlineHandler);
            window.removeEventListener('offline', this.onlineHandler);
        }
        this.listeners.clear();
    }

    /**
     * Start periodic connectivity monitoring
     * @param {number} interval - Check interval in milliseconds (default: 30000)
     */
    startMonitoring(interval = 30000) {
        this.stopMonitoring(); // Clear any existing interval

        console.log(`${LOG_PREFIX} 🔄 Starting periodic connectivity monitoring every ${interval}ms`);
        this.monitoringInterval = setInterval(() => {
            if (this.isOnline) {
                console.log(`${LOG_PREFIX} 🕒 Periodic check triggered`);
                this.checkInternetConnection();
            } else {
                console.log(`${LOG_PREFIX} 🕒 Periodic check skipped (navigator offline)`);
            }
        }, interval);
    }

    /**
     * Stop periodic connectivity monitoring
     */
    stopMonitoring() {
        if (this.monitoringInterval) {
            clearInterval(this.monitoringInterval);
            this.monitoringInterval = null;
            console.log(`${LOG_PREFIX} ⏹️ Stopped periodic connectivity monitoring`);
        }
    }
}

// Create and export singleton instance
const internetConnectivityService = new InternetConnectivityService();

export default internetConnectivityService;

// Export class for testing
export { InternetConnectivityService };